import { formatCreatedDate, getFilePreviewType, type Asset } from '@odo/shared-ui';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { SubmissionFileAccess } from './useSubmissionFileAccess';

interface SubsectionHeadingProps {
    title: string;
    complete: boolean;
}

/** Section title with a green check once that part is complete. */
export function SubsectionHeading({ title, complete }: SubsectionHeadingProps) {
    return (
        <h2 className="flex items-center gap-2 mb-4 text-xl font-bold text-gray-900">
            <span>{title}</span>
            {complete && (
                <>
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    <span className="sr-only">Complete</span>
                </>
            )}
        </h2>
    );
}

interface ReadOnlyInputProps {
    label: string;
    value: string | null | undefined;
    required?: boolean;
    /** Shown under the value when it is present but fails validation. */
    invalidHint?: string | null;
    multiline?: boolean;
}

/** A creation-flow form input rendered read-only, with a "Not provided" state. */
export function ReadOnlyInput({
    label,
    value,
    required = true,
    invalidHint,
    multiline = false
}: ReadOnlyInputProps) {
    const provided = (value ?? '').trim().length > 0;
    const isInvalid = provided && Boolean(invalidHint);
    const border = isInvalid ? 'border-red-500' : 'border-gray-300';
    const shape = multiline
        ? `min-h-32 p-4 rounded-lg whitespace-pre-wrap ${provided ? border : 'border-red-400'}`
        : `flex items-center min-h-10 px-3 py-2 rounded-md ${border}`;

    return (
        <div className="flex flex-col min-w-0">
            <span className="block text-sm font-medium text-gray-700 mb-2">
                {label}
                {required && ' *'}
            </span>
            <div className={`w-full border bg-white text-sm ${shape}`}>
                {provided ? (
                    <span className="min-w-0 text-gray-800 [overflow-wrap:anywhere]">{value}</span>
                ) : (
                    <span className="italic text-gray-400">Not provided</span>
                )}
            </div>
            {isInvalid && <p className="text-red-500 text-sm mt-1">{invalidHint}</p>}
        </div>
    );
}

interface UploadedFileRowProps {
    asset: Asset;
    access: SubmissionFileAccess;
    /** Optional sub-label, e.g. the STL arch or radiograph type. */
    caption?: string | null;
    /** Leading icon; image files show their thumbnail instead when available. */
    icon?: string;
}

/**
 * An uploaded file listed in the treatment step. Images open in the in-app
 * viewer; other types (STL, CBCT archives) are listed only, never downloaded.
 */
export function UploadedFileRow({ asset, access, caption, icon }: UploadedFileRowProps) {
    const isImage = getFilePreviewType(asset.name) === 'image';
    const thumb = isImage ? access.thumbnailFor(asset) : null;
    const isOpening = access.openingId !== null && access.openingId === asset.patientFileId;
    const detail = [caption, formatCreatedDate(asset.createdAt)].filter(Boolean).join(' · ');
    const rowClass = 'w-full flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 text-left';

    const content = (
        <>
            <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
            {thumb ? (
                <img
                    src={thumb}
                    alt=""
                    onError={() => access.onThumbnailError(asset)}
                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                />
            ) : (
                icon && <img src={icon} alt="" className="w-8 h-8 flex-shrink-0" />
            )}
            <div className="flex flex-col min-w-0">
                <span className="text-sm text-gray-800 truncate">{asset.name}</span>
                <span className="text-xs text-gray-500 truncate">
                    {isOpening ? 'Opening...' : detail}
                </span>
            </div>
        </>
    );

    if (!isImage) return <div className={rowClass}>{content}</div>;

    return (
        <button
            type="button"
            onClick={() => access.openFile(asset)}
            disabled={isOpening}
            title={`View ${asset.name}`}
            className={`${rowClass} hover:bg-gray-100 transition-colors disabled:opacity-60`}
        >
            {content}
        </button>
    );
}
